import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useState, useRef, useContext } from "react";
import useSessionStorage from "../hooks/useSessionStorage";
import CategoryList from "./CategoryList";
import ProductList from "./ProductList";
import RatingSection from "./RatingSection";
import { useDispatch, useSelector } from "react-redux";
import { getCategory } from "../state/categorySlice";
import { getProduct } from "../state/productSlice";
import { getRestaurant } from "../state/restaurantSlice";
import { useLocation, Link, useNavigationType } from "react-router-dom";
import down from "../assets/icon/down.png";
import { LanguageContext } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import right from "../assets/icon/right.png";
import "../styles/backtotop.css";

const Body = () => {
  const { language } = useContext(LanguageContext);
  const theme = useTheme();
  const navigationType = useNavigationType();
  const dispatch = useDispatch();
  const { product, isLoading: productLoading, loadedId: productLoadedId } = useSelector(
    (state) => state.product
  );
  const { restaurant, loadedId: restaurantLoadedId } = useSelector((state) => state.restaurant);
  const location = useLocation();
  const upCategory = new URLSearchParams(location.search).get(
    "selectedCategory"
  );
  const id = new URLSearchParams(location.search).get(
    "id"
  );
  const { category, isLoading, loadedId: categoryLoadedId } = useSelector((state) => state.category);
  const serachRef = useRef();

  // console.log('category',category)

  const [chooseCategory, setChooseCategory] = useSessionStorage(
    `chooseCategory:${upCategory || "all"}`,
    undefined
  );
  const [visible, setVisible] = useState(false);
  const [menuModal, setMenuModal] = useState(false);
  // Persist id (userCode) when Body mounts and repair URL if needed
  useEffect(() => {
    if (id) {
      try { window.sessionStorage.setItem('userCode', id); } catch {}
      const params = new URLSearchParams(window.location.search);
      if (!params.get('id')) {
        params.set('id', id);
        window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
      }
    }
  }, [id]);

  useEffect(() => {
    // Fetch only if store is empty OR cached data belongs to a different id
    const needProducts = !product?.length || productLoadedId !== id;
    const needCategories = !category?.length || categoryLoadedId !== id;
    const needRestaurant = !restaurant?.length || restaurantLoadedId !== id;

    if (needProducts) {
      dispatch(getProduct({ path: id + "/productsno.json", id }));
    }
    if (needCategories) {
      dispatch(getCategory({ path: id + "/categoryno.json", id }));
    }
    if (needRestaurant) {
      dispatch(getRestaurant({ path: id + "/restaurant.json", id }));
    }
  }, [dispatch, id, product?.length, category?.length, restaurant?.length, productLoadedId, categoryLoadedId, restaurantLoadedId]);

  const filterProducts = (category) => {
    setChooseCategory(category);
    const el = document.getElementById(category);
    if (el) {
      const y = el.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", () => {
      if (window.scrollY > 100) {
        setVisible(true);
      } else {
        setVisible(false);
      }
    });
    const handleScroll = () => {
      const scrolledElement = document.elementFromPoint(
        window.innerWidth / 2,
        window.innerHeight / 2
      );
      if (scrolledElement && scrolledElement.id) {
        const category = scrolledElement.id;
        setChooseCategory(category);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Kategorileri dile göre gruplama
  const groupby = category?.reduce((group, item) => {
    const groupKey =
      language === "Türkçe"
        ? item.UP_CATEGORY_TR
        : item.UP_CATEGORY_IN?.trim() || item.UP_CATEGORY_TR;

    group[groupKey] = group[groupKey] ?? [];
    group[groupKey].push(item);

    return group;
  }, {});

  // Alt kategorileri getirme
  const getSubCategories = (upperCategory) => {
    const matchedCategories = Object.values(groupby).find((categories) => {
      const firstItem = categories[0];
      const groupKey =
        language === "Türkçe"
          ? firstItem.UP_CATEGORY_TR
          : firstItem.UP_CATEGORY_IN?.trim() || firstItem.UP_CATEGORY_TR;

      return groupKey === upperCategory;
    });

    return matchedCategories || [];
  };

  // Alt kategori ürünlerini filtreleme
  const subCategoriesProducts = category
    .filter((cat) => {
      const groupKey =
        language === "Türkçe"
          ? cat.UP_CATEGORY_TR
          : cat.UP_CATEGORY_IN?.trim() || cat.UP_CATEGORY_TR;
      return groupKey === upCategory;
    })
    .map((subCat) =>
      product.filter((prod) => prod.CATEGORY === subCat.CATEGORY)
    )
    .flat();

  const backtotop = () => {
    window.scrollTo(0, 0);
  };
  useEffect(() => {
    // Restore scroll position from sessionStorage for this route (if available)
    const scrollKey = `scroll:${location.pathname}${location.search}`;
    const saved = window.sessionStorage.getItem(scrollKey);
    if (saved && !isNaN(Number(saved))) {
      // Only restore on POP (back/forward) or hard reload; App already avoids scrollTo(0,0) on POP
      window.scrollTo(0, Number(saved));
    } else if (navigationType !== "POP") {
      // Fresh navigations go to top
      window.scrollTo(0, 0);
    }

    // Visibility toggle and scroll saving
    const onScroll = () => {
      // Save scroll position (throttled via requestAnimationFrame)
      if (!onScroll.raf) {
        onScroll.raf = requestAnimationFrame(() => {
          try {
            window.sessionStorage.setItem(scrollKey, String(window.scrollY));
          } catch {}
          onScroll.raf = null;
        });
      }

      if (window.scrollY > 500) {
        setVisible(true);
      } else {
        setVisible(false);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (onScroll.raf) cancelAnimationFrame(onScroll.raf);
    };
  }, [navigationType, location.pathname, location.search]);

  return (
    <div className="bg-white relative py-2">
      {/* up category - sadece birden fazla üst kategori varsa göster */}
      {groupby && Object.keys(groupby).length > 1 && (
        <div
          onClick={() => setMenuModal(true)}
          className="d-flex flex-row justify-center items-center mt-2"
        >
          <p className="font-bold text-xl text-zinc-600">{upCategory}</p>
          <img src={down} alt="img" className="w-6 h-6 ml-2 mb-3" />
        </div>
      )}

      {/* search bar */}
      <Link
        to={`/search`}
        className="flex gap-2 md:gap-4 max-w-[560px] w-[90%] mx-auto mt-2 mb-6 no-underline"
      >
        <input
          type="search"
          name="search"
          id="search"
          placeholder={language === "Türkçe" ? "Ara" : "Search"}
          className="p-2 px-4 rounded-md border outline-none focus-within:border-orange-400 border-gray-200 grow w-full"
          ref={serachRef}
        />
        <button className={`${theme.primary.bg} basis-2/12 text-center text-white p-2 flex justify-center gap-2 items-center md:px-8 rounded-md text-sm md:text-base`}>
          <MagnifyingGlassIcon className="w-4 h-4" />{" "}
          <span className="hidden md:block">
            {language === "Türkçe" ? "Ara" : "Search"}
          </span>
        </button>
      </Link>

      {/* Category list */}
      <CategoryList
        category={getSubCategories(upCategory)}
        isLoading={isLoading}
        filterProducts={filterProducts}
        id={id}
        chooseCategory={chooseCategory}
        visible={visible}
        language={language}
      />

      <ProductList
        language={language}
        isLoading={productLoading}
        // products={!!list?.length ? list : subCategoriesProducts}
        products={subCategoriesProducts}
        id={id}
      />

      {/* Rating Section */}
      <RatingSection id={id} />

      {visible && (
        <button className="backtotop" onClick={backtotop}>
          <i className="fa-solid fa-circle-arrow-up fixed bottom-10 right-10"></i>
        </button>
      )}

      {/* modal menu */}
      <Modal
        className="my-modal"
        centered
        size="md"
        isOpen={menuModal}
        toggle={() => setMenuModal(!menuModal)}
      >
        <ModalHeader toggle={() => setMenuModal(!menuModal)}>
          {language === "Türkçe" ? <p>Menü Seçin</p> : <p>Select a Menu</p>}
        </ModalHeader>
        <ModalBody>
          <div
            className="modalmenu-container"
            onClick={() => setMenuModal(false)}
          >
            {Object.keys(groupby)?.map((item, index) => {
              return (
                <Link key={index} to={`/home?id=${id}&selectedCategory=${item}`}>
                  <div className="border-b d-flex flex-row justify-between items-center">
                    <p className="p-1 mt-3">{item}</p>
                    <img src={right} className="right-logo w-6 h-6" alt="img" />
                  </div>
                </Link>
              );
            })}
          </div>
        </ModalBody>
        <ModalFooter className="border-0">
          {language === "Türkçe" ? (
            <Button
              color="btn btn-outline-secondary"
              onClick={() => setMenuModal(!menuModal)}
            >
              Kapat
            </Button>
          ) : (
            <Button
              color="btn btn-outline-secondary"
              onClick={() => setMenuModal(!menuModal)}
            >
              Close
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
};
export default Body;
